import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Menu as AntMenu, Tag, Table, Grid, Modal, Tooltip, message } from 'antd';
import {
    EditOutlined,
    SwapOutlined,
    FilePdfOutlined,
    StopOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Menu from '../../components/Menu/Menu';
import { listService } from '../../services/ListService';
import './Assignments.scss';
import ListStatusGuard from './ListStatusGuard';

const StatusDot = ({ active }) => (
    <span
        style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: active ? '#52c41a' : '#ff4d4f',
            marginRight: 6,
        }}
    />
);

const Assignments = () => {
    const { id } = useParams(); // necessity_list_id
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [listStatus, setListStatus] = useState('manual_processing');
    const [blockSupplierRecord, setBlockSupplierRecord] = useState(null);
    const [isBlockingSupplier, setIsBlockingSupplier] = useState(false);
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const getSupplierAssignedCount = supplierList => {
        if (!supplierList) return 0;

        const possibleCounts = [
            supplierList.counts?.total,
            supplierList.counts?.assigned,
            supplierList.total,
            supplierList.assigned,
            supplierList.items_count,
            supplierList.products_count
        ];

        const numericCount = possibleCounts.find(
            value => typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')
        );
        if (numericCount !== undefined) {
            const parsedCount = Number(numericCount);
            if (!Number.isNaN(parsedCount)) return parsedCount;
        }

        if (Array.isArray(supplierList.items)) return supplierList.items.length;
        if (Array.isArray(supplierList.products)) return supplierList.products.length;

        return 0;
    };

    useEffect(() => {
        fetchAssignments();
    }, [id]);

    const fetchAssignments = () => {
        setLoading(true);

        listService.getManualProcessingSuppliers(id)
            .then(res => {
                const { supplier_lists, overall_counts } = res.data;
                setListStatus(res.data?.necessity_list?.status || res.data?.status || 'manual_processing');

                const unassignedRow = {
                    id: 'unassigned',
                    isUnassigned: true,
                    title: 'NEATRIBUITE',
                    prepared: false,
                    counts: {
                        total: overall_counts?.unassigned ?? 0
                    }
                };

                const filteredSuppliers = (supplier_lists || []).filter(
                    supplierList => !!supplierList?.supplier?.id && getSupplierAssignedCount(supplierList) > 0
                );

                setData([unassignedRow, ...filteredSuppliers]);
            })
            .finally(() => setLoading(false));
    };

    const logBlockDebugInfo = (supplierListRecord, source) => {
        if (process.env.NODE_ENV === 'production') {
            return;
        }

        const supplierStatus = ListStatusGuard.getManualSupplierStatus(supplierListRecord);
        const pendingItemsCount = ListStatusGuard.getPendingItemsCount(supplierListRecord);
        const canBlock = ListStatusGuard.canBlockSupplierList(listStatus, supplierListRecord);
        const supplierPathId = supplierListRecord?.supplier?.id ?? supplierListRecord?.id;
        let disabledReason = '';
        if (listStatus !== 'manual_processing') {
            disabledReason = 'list-status-not-manual-processing';
        } else if (supplierStatus !== 'open') {
            disabledReason = 'supplier-list-not-open';
        } else if (pendingItemsCount > 0) {
            disabledReason = 'pending-items-exist';
        }

        const debugPayload = {
            source,
            listId: id,
            supplierListId: supplierListRecord?.id,
            supplierPathId,
            supplierName: supplierListRecord?.supplier?.name,
            listStatus,
            supplierStatus,
            pendingItemsCount,
            canBlock,
            disabledReason,
        };

        console.log('[Assignments:block-debug]', JSON.stringify(debugPayload));
    };

    const getSupplierPathId = (supplierListRecord) => (
        supplierListRecord?.supplier?.id ?? supplierListRecord?.id
    );

    const openBlockSupplierModal = (supplierListRecord) => {
        logBlockDebugInfo(supplierListRecord, 'openBlockSupplierModal');

        if (!ListStatusGuard.canBlockSupplierList(listStatus, supplierListRecord)) {
            return;
        }

        setBlockSupplierRecord(supplierListRecord);
    };

    const handleCancelBlockSupplier = () => {
        if (isBlockingSupplier) {
            return;
        }
        setBlockSupplierRecord(null);
    };

    const handleConfirmBlockSupplier = () => {
        if (!blockSupplierRecord) {
            return;
        }

        setIsBlockingSupplier(true);

        const supplierPathId = getSupplierPathId(blockSupplierRecord);

        listService
            .blockManualProcessingSupplier(id, supplierPathId)
            .then((res) => {
                const successMessage = res?.data?.message || 'Supplier list blocked successfully';
                message.success(successMessage);
                setBlockSupplierRecord(null);
                fetchAssignments();
            })
            .finally(() => {
                setIsBlockingSupplier(false);
            });
    };

    const getBlockDisabledReason = (supplierListRecord) => {
        if (listStatus !== 'manual_processing') {
            return 'Blocarea este disponibilă doar în etapa de procesare manuală.';
        }

        if (ListStatusGuard.getManualSupplierStatus(supplierListRecord) !== 'open') {
            return 'Lista furnizorului este deja blocată.';
        }

        if (ListStatusGuard.getPendingItemsCount(supplierListRecord) > 0) {
            return 'Blocarea este disponibilă doar când nu mai există produse în așteptare.';
        }

        return '';
    };

    const handleDownloadManualProcessingPdf = (supplierListRecord) => {
        const supplierId = supplierListRecord?.supplier?.id ?? supplierListRecord?.id;
        if (!supplierId || supplierListRecord?.isUnassigned) {
            message.warning('Nu există furnizor valid pentru descărcarea PDF-ului.');
            return;
        }

        const loadingMessageKey = `manual-processing-pdf-${id}-${supplierId}`;
        message.loading({
            content: 'Se generează PDF-ul...',
            key: loadingMessageKey,
            duration: 0,
        });

        listService
            .downloadManualProcessingPdf(id, supplierId)
            .then((response) => {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const link = document.createElement('a');
                const objectUrl = window.URL.createObjectURL(blob);
                link.href = objectUrl;
                link.download = `lista-${id}-furnizor-${supplierId}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(objectUrl);

                message.success({
                    content: 'PDF descărcat cu succes.',
                    key: loadingMessageKey,
                });
            })
            .catch(() => {
                message.error({
                    content: 'Eroare la descărcarea PDF-ului.',
                    key: loadingMessageKey,
                });
            });
    };

    const renderActions = (_, record) => {
        if (record.isUnassigned) {
            return (
                <Button
                    type="link"
                    onClick={() =>
                        navigate(`/atribuiri/${id}/furnizor/${record.id}`)
                    }
                >
                    Vezi produse
                </Button>
            );
        }

        const canTogglePrepared = ListStatusGuard.canTogglePrepared(listStatus, record);
        const canBlockSupplierList = ListStatusGuard.canBlockSupplierList(listStatus, record);
        const blockDisabledReason = canBlockSupplierList ? '' : getBlockDisabledReason(record);

        const menu = (
            <AntMenu
                items={[
                    {
                        key: 'edit',
                        label: 'Vezi/Editează',
                        icon: <EditOutlined />,
                        onClick: () =>
                            navigate(`/atribuiri/${id}/furnizor/${record.supplier.id}`)
                    },
                    {
                        key: 'toggle',
                        label: record.prepared
                            ? 'Marchează ca nepregătit'
                            : 'Marchează ca pregătit',
                        icon: <SwapOutlined />,
                        disabled: !canTogglePrepared,
                        onClick: () =>
                            listService
                                .updateManualProcessingStatus(
                                    id,
                                    record.supplier.id,
                                    !record.prepared
                                )
                                .then(fetchAssignments)
                    },
                    {
                        key: 'pdf',
                        label: 'Descarcă PDF',
                        icon: <FilePdfOutlined />,
                        onClick: () => handleDownloadManualProcessingPdf(record)
                    },
                    { type: 'divider' },
                    {
                        key: 'block',
                        label: canBlockSupplierList ? (
                            'Blochează lista'
                        ) : (
                            <Tooltip title={blockDisabledReason} placement="left">
                                <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>Blochează lista</span>
                            </Tooltip>
                        ),
                        icon: <StopOutlined />,
                        danger: canBlockSupplierList,
                        className: !canBlockSupplierList ? 'assignments__block-disabled-item' : undefined,
                        disabled: !canBlockSupplierList,
                        onClick: () => {
                            logBlockDebugInfo(record, 'block-menu-click');
                            openBlockSupplierModal(record);
                        }
                    }
                ]}
            />
        );

        return (
            <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
                <Button>⋮</Button>
            </Dropdown>
        );
    };

    const columns = [
        {
            title: 'Nume',
            key: 'name',
            render: (_, record) =>
                record.isUnassigned
                    ? <strong>NEATRIBUITE</strong>
                    : record.supplier?.name
        },
        {
            title: 'MAIL / WA',
            key: 'contact',
            render: (_, record) =>
                record.isUnassigned
                    ? '—'
                    : (
                        <>
                            <StatusDot active={false} /> Mail
                            <br />
                            <StatusDot active={false} /> WhatsApp
                        </>
                    )
        },
        {
            title: 'Pregătit',
            key: 'prepared',
            render: (_, record) =>
                record.isUnassigned
                    ? '—'
                    : record.prepared
                        ? <Tag color="green">DA</Tag>
                        : <Tag color="red">NU</Tag>
        },
        {
            title: 'Opțiuni',
            key: 'actions',
            width: 120,
            render: renderActions
        }
    ];

    const MobileList = () => {
        if (data.length === 0) {
            return (
                <div className="list__empty-state">
                    <p className="list__empty-state-text">Nu există furnizori atribuiți.</p>
                </div>
            );
        }

        return (
            <div className="list__mobile-table-wrapper">
                <div className="list__mobile-table-header">
                    <div className="list__mobile-table-header-cell list__mobile-table-header-name">Nume</div>
                    <div className="list__mobile-table-header-cell" style={{ width: '90px', flexShrink: 0 }}>Pregătit</div>
                    <div className="list__mobile-table-header-cell list__mobile-table-header-actions" style={{ width: '60px', flexShrink: 0 }}>Opt</div>
                </div>
                <div className="list__mobile-list">
                    {data.map((row) => (
                        <div key={row.id} className="list__mobile-card">
                            <div className="list__mobile-card-cell list__mobile-card-name">
                                {row.isUnassigned ? <strong>NEATRIBUITE</strong> : row.supplier?.name}
                            </div>
                            <div className="list__mobile-card-cell" style={{ width: '90px', flexShrink: 0 }}>
                                {row.isUnassigned ? '—' : row.prepared ? <Tag color="green">DA</Tag> : <Tag color="red">NU</Tag>}
                            </div>
                            <div className="list__mobile-card-cell list__mobile-card-actions" style={{ width: '60px', flexShrink: 0 }}>
                                {renderActions(null, row)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="standard products list assignments-page">
            <Header />

            <div className="standard__container list assignments-page__container">
                <Menu />
                <div className="standard__wrapper assignments-page__wrapper">
                    <div style={{ padding: 24 }}>
                        <h2 className="standard__page-title">
                            Vezi atribuiri – furnizori
                        </h2>
                        <h4 className="standard__page-info">
                            Furnizorii atribuiți listei și statusul trimiterilor.
                        </h4>

                        {!isMobile ? (
                            <Table
                                dataSource={data}
                                columns={columns}
                                rowKey="id"
                                loading={loading}
                                pagination={false}
                                scroll={{ x: true }}
                            />
                        ) : (
                            <MobileList />
                        )}
                        <div className='list__back'>
                            <Button
                                type="link"
                                icon={<ArrowLeftOutlined />}
                                onClick={() => navigate(-1)}
                                style={{ padding: 0, color: 'initial' }}
                            >
                                Înapoi
                            </Button>
                        </div>

                    </div>
                </div>
            </div>

            <Modal
                title="Blochezi această listă per furnizor?"
                open={Boolean(blockSupplierRecord)}
                onOk={handleConfirmBlockSupplier}
                onCancel={handleCancelBlockSupplier}
                okText="Blochează"
                cancelText="Anulează"
                okButtonProps={{ danger: true }}
                confirmLoading={isBlockingSupplier}
            >
                După blocare, lista pentru {blockSupplierRecord?.supplier?.name || 'acest furnizor'} nu mai poate fi modificată.
            </Modal>
        </div>
    );
};

export default Assignments;